import { messageFromFailedResponse, parseFastApiDetail } from "lib/utils/userFacingApiError";
import { toUtcMissionOverviewDateRange } from "./missionOverviewRange";
/**
 * Typed client for the customer-hosted `fleet_mission_storage` read API,
 * reached through `arc_proxy`'s `/api/fleet-mission-storage/*` forward-auth
 * route.
 *
 * The proxy strips exactly `/api/fleet-mission-storage` and the read API's own
 * routes live under `/api`, so the browser path carries the segment twice:
 * `/api/fleet-mission-storage/api/missions`. That is correct, not a typo.
 *
 * Authentication is the organization browser JWT already held by
 * `OrgServerClient` — the proxy hands it to `org_server /auth/validate` and
 * replaces it with an `X-Fleet-Org-ID` assertion before the read API sees the
 * request. The browser therefore never sends, and must never send, any
 * `X-Fleet-*` identity header itself.
 *
 * Response shapes are identical to the Org Server GraphQL ones, so the
 * `Org*` types are reused rather than duplicated.
 */

import { refresh } from "lib/orgServer/OrgServerClient";
import type {
    OrgMission,
    OrgMissionDetails,
    OrgMissionOverview,
    OrgMissionRun,
} from "lib/orgServer/OrgServerClient";

export type {
    OrgMission,
    OrgMissionAction,
    OrgMissionDetails,
    OrgMissionOverview,
    OrgMissionRun,
} from "lib/orgServer/OrgServerClient";

export type FleetMissionOverview = OrgMissionOverview & {
    runsThisMonth: number;
    completedRuns: number;
    averageDurationSeconds: number | null;
};

export type FleetMissionOverviewDateRange = {
    /** Inclusive browser-local ISO calendar date (`YYYY-MM-DD`). */
    startDate: string;
    /** Inclusive browser-local ISO calendar date (`YYYY-MM-DD`). */
    endDate: string;
};

export type FleetMissionRunLog = {
    id: string;
    topic: string;
    background: boolean;
    notes: string;
    pose: unknown;
    data: unknown;
    createdAt: string;
    attachments: Array<{
        id: string;
        fileName: string;
        mimeType: string;
        size: number | null;
        /**
         * Import state of the copied bytes:
         * waiting_source | pending | fetching | stored | unavailable | skipped | failed
         */
        fetchStatus: string;
        /** True only when fetchStatus is "stored" — the bytes are on disk. */
        available: boolean;
    }>;
    videoClipJobs: Array<{
        id: string;
        status: string;
        attachmentId: string | null;
    }>;
};

export type FleetMissionRunRecording = {
    fileName: string;
    mimeType: string;
    size: number | null;
    fetchStatus: string;
    available: boolean;
};

export type FleetMissionRunDetails = OrgMissionRun & {
    hasRecording: boolean;
    recording: FleetMissionRunRecording | null;
    logs: FleetMissionRunLog[];
};

export type FleetMissionRunLogsPage = {
    logs: FleetMissionRunLog[];
    nextCursor: string | null;
};

export type FleetMissionRunRecordingStatus = {
    hasRecording: boolean;
    recording: FleetMissionRunRecording | null;
};

export type FleetMissionRunRefetchResponse = {
    accepted: boolean;
    status: string;
    resetMediaRows: number;
};

export type FleetMissionRunIdentity = {
    sourceArcSerial: string;
    sourceRunId: string;
};

export type FleetMissionRunDeleteResponse = {
    accepted: boolean;
    deletedRunCount: number;
};

export type FleetMissionRecordingTicket = {
    ticket: string;
    expiresAt: string;
    url: string;
};

/** Public path prefix owned by arc_proxy. */
export const FLEET_MISSION_STORAGE_PREFIX = "/api/fleet-mission-storage";

/**
 * The read API clamps `limit` to 500 server-side. Asking for more is silently
 * truncated, so 500 is the honest maximum; page with `offset` beyond that.
 */
export const MAX_PAGE_SIZE = 500;

/**
 * Base URL for the organization's read API, or `null` when the organization
 * has no proxy assigned.
 *
 * Fleet traffic is always https, including when the React dev server itself is
 * plain http — same rule as `originForHost()` in lib/consts/arcGateway.ts.
 */
export const fleetMissionStorageBaseUrl = (proxyHostname: string | null | undefined): string | null => {
    const host = proxyHostname?.trim();
    if (!host) return null;
    const normalized = host.replace(/^[a-z][a-z\d+.-]*:\/\//i, "").replace(/\/+$/, "");
    if (!normalized) return null;
    return `https://${normalized}${FLEET_MISSION_STORAGE_PREFIX}`;
};

const requireBaseUrl = (proxyHostname: string | null | undefined): string => {
    const baseUrl = fleetMissionStorageBaseUrl(proxyHostname);
    if (!baseUrl) throw new Error("No fleet proxy configured");
    return baseUrl;
};

const buildUrl = (baseUrl: string, path: string, params?: Record<string, string | number>): string => {
    const query = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([key, value]) => query.set(key, String(value)));
    const search = query.toString();
    return `${baseUrl}${path}${search ? `?${search}` : ""}`;
};

const authHeaders = (): HeadersInit => {
    const token = localStorage.getItem("orgUserToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const requestOnce = (url: string, signal?: AbortSignal): Promise<Response> =>
    fetch(url, {
        method: "GET",
        headers: authHeaders(),
        cache: "no-store",
        mode: "cors",
        // The JWT travels as a header; omitting credentials keeps the proxy's
        // CORS response free of Access-Control-Allow-Credentials.
        credentials: "omit",
        signal,
    });

const postOnce = (url: string, signal?: AbortSignal, body?: unknown): Promise<Response> =>
    fetch(url, {
        method: "POST",
        headers: body === undefined
            ? authHeaders()
            : { ...authHeaders(), "Content-Type": "application/json" },
        cache: "no-store",
        mode: "cors",
        credentials: "omit",
        signal,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

const errorFor = async (res: Response): Promise<Error> => {
    let detail: string | undefined;
    try {
        detail = parseFastApiDetail((await res.json()) as { detail?: unknown });
    } catch {
        /* body was not JSON — the status alone is the message */
    }
    return new Error(
        messageFromFailedResponse(
            res.status,
            detail,
            "Couldn't complete the request. Try again."
        )
    );
};

type GetJsonOptions = {
    /** Resolve to `null` on 404 instead of throwing. Details endpoint only. */
    nullOn404?: boolean;
    signal?: AbortSignal;
};

/**
 * GET one JSON document, retrying exactly once behind a token refresh.
 *
 * A second 401 throws rather than looping: `refresh()` already rotated the
 * token, so a repeat failure means the session is genuinely gone and
 * `OrgFleetProvider`'s login modal is the recovery path.
 */
const getJson = async <T>(
    baseUrl: string,
    path: string,
    params?: Record<string, string | number>,
    options: GetJsonOptions = {}
): Promise<T | null> => {
    const url = buildUrl(baseUrl, path, params);

    let res = await requestOnce(url, options.signal);
    if (res.status === 401) {
        await refresh(); // throws "401 ..." when there is no usable refresh token
        res = await requestOnce(url, options.signal);
    }

    if (res.status === 401) throw new Error("Sign in required.");
    if (res.status === 403) throw new Error("You don't have permission to do that.");
    if (res.status === 404 && options.nullOn404) return null;
    if (!res.ok) throw await errorFor(res);

    try {
        return (await res.json()) as T;
    } catch {
        throw new Error("Malformed response");
    }
};

const clampPageSize = (limit: number): number => Math.max(0, Math.min(limit, MAX_PAGE_SIZE));

const missionOverviewRangeParams = (
    range?: FleetMissionOverviewDateRange,
): Record<string, string> | undefined => {
    if (!range) return undefined;
    const { startAt, endAt } = toUtcMissionOverviewDateRange(range);
    return { start_date: startAt, end_date: endAt };
};

// Every reader below is `async` on purpose: a missing proxy hostname must
// surface as a *rejected promise*, not a synchronous throw. Callers use
// `.then().catch()`, and a synchronous throw would escape that chain and take
// the effect down with it.

export const getMissions = async (
    proxyHostname: string | null | undefined,
    limit = MAX_PAGE_SIZE,
    offset = 0
): Promise<OrgMission[]> =>
    (await getJson<OrgMission[]>(requireBaseUrl(proxyHostname), "/api/missions", {
        limit: clampPageSize(limit),
        offset: Math.max(0, offset),
    })) as OrgMission[];

export const getMission = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceMissionId: string
): Promise<OrgMissionDetails | null> =>
    getJson<OrgMissionDetails>(
        requireBaseUrl(proxyHostname),
        `/api/missions/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceMissionId)}`,
        undefined,
        { nullOn404: true }
    );

export const getMissionOverview = async (
    proxyHostname: string | null | undefined,
    range?: FleetMissionOverviewDateRange,
): Promise<FleetMissionOverview> =>
    (await getJson<FleetMissionOverview>(
        requireBaseUrl(proxyHostname),
        "/api/overview",
        missionOverviewRangeParams(range),
    )) as FleetMissionOverview;

export const getRuns = async (
    proxyHostname: string | null | undefined,
    limit = MAX_PAGE_SIZE,
    offset = 0,
    range?: FleetMissionOverviewDateRange,
): Promise<OrgMissionRun[]> =>
    (await getJson<OrgMissionRun[]>(requireBaseUrl(proxyHostname), "/api/runs", {
        limit: clampPageSize(limit),
        offset: Math.max(0, offset),
        ...(missionOverviewRangeParams(range) ?? {}),
    })) as OrgMissionRun[];

export const getRun = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceRunId: string,
): Promise<FleetMissionRunDetails | null> =>
    getJson<FleetMissionRunDetails>(
        requireBaseUrl(proxyHostname),
        `/api/runs/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceRunId)}`,
        undefined,
        { nullOn404: true },
);

export const getRunLogs = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceRunId: string,
    options: { limit?: number; cursor?: string | null; signal?: AbortSignal } = {},
): Promise<FleetMissionRunLogsPage | null> => {
    const params: Record<string, string | number> = {
        limit: Math.max(1, clampPageSize(options.limit ?? MAX_PAGE_SIZE)),
    };
    if (options.cursor) params.cursor = options.cursor;
    return getJson<FleetMissionRunLogsPage>(
        requireBaseUrl(proxyHostname),
        `/api/runs/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceRunId)}/logs`,
        params,
        { nullOn404: true, signal: options.signal },
    );
};

export const getRunRecordingStatus = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceRunId: string,
    signal?: AbortSignal,
): Promise<FleetMissionRunRecordingStatus | null> =>
    getJson<FleetMissionRunRecordingStatus>(
        requireBaseUrl(proxyHostname),
        `/api/runs/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceRunId)}/recording-status`,
        undefined,
        { nullOn404: true, signal },
    );

export const refetchRun = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceRunId: string,
    signal?: AbortSignal,
): Promise<FleetMissionRunRefetchResponse | null> => {
    const baseUrl = requireBaseUrl(proxyHostname);
    const requestUrl = `${baseUrl}/api/runs/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceRunId)}/refetch`;
    let res = await postOnce(requestUrl, signal);
    if (res.status === 401) {
        await refresh();
        res = await postOnce(requestUrl, signal);
    }
    if (res.status === 401) throw new Error("Sign in required.");
    if (res.status === 403) throw new Error("You don't have permission to do that.");
    if (res.status === 404) return null;
    if (!res.ok) throw await errorFor(res);
    try {
        return (await res.json()) as FleetMissionRunRefetchResponse;
    } catch {
        throw new Error("Malformed response");
    }
};

export const deleteRuns = async (
    proxyHostname: string | null | undefined,
    runIds: FleetMissionRunIdentity[],
    signal?: AbortSignal,
): Promise<FleetMissionRunDeleteResponse> => {
    const baseUrl = requireBaseUrl(proxyHostname);
    const requestUrl = `${baseUrl}/api/runs/delete`;
    const body = { runIds };
    let res = await postOnce(requestUrl, signal, body);
    if (res.status === 401) {
        await refresh();
        res = await postOnce(requestUrl, signal, body);
    }
    if (res.status === 401) throw new Error("401 Unauthorized");
    if (res.status === 403) throw new Error("403 Forbidden");
    if (!res.ok) throw await errorFor(res);
    try {
        const response = (await res.json()) as Partial<FleetMissionRunDeleteResponse>;
        if (response.accepted !== true || typeof response.deletedRunCount !== "number") {
            throw new Error("Malformed response");
        }
        return {
            accepted: response.accepted,
            deletedRunCount: response.deletedRunCount,
        };
    } catch (error) {
        if (error instanceof Error && error.message === "Malformed response") throw error;
        throw new Error("Malformed response");
    }
};

export const createRecordingTicket = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    sourceRunId: string,
    signal?: AbortSignal,
): Promise<FleetMissionRecordingTicket | null> => {
    const baseUrl = requireBaseUrl(proxyHostname);
    const path = `/api/runs/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(sourceRunId)}/recording-ticket`;
    const requestUrl = `${baseUrl}${path}`;
    let res = await postOnce(requestUrl, signal);
    if (res.status === 401) {
        await refresh();
        res = await postOnce(requestUrl, signal);
    }
    if (res.status === 401) throw new Error("Sign in required.");
    if (res.status === 403) throw new Error("You don't have permission to do that.");
    if (res.status === 404) return null;
    if (!res.ok) throw await errorFor(res);
    const body = (await res.json()) as { ticket?: unknown; expiresAt?: unknown; path?: unknown };
    if (typeof body.ticket !== "string" || typeof body.expiresAt !== "string" || typeof body.path !== "string") {
        throw new Error("Malformed recording ticket response");
    }
    const expectedPrefix = `${FLEET_MISSION_STORAGE_PREFIX}/api/runs/`;
    if (!body.path.startsWith(expectedPrefix) || !body.path.endsWith("/recording")) {
        throw new Error("Invalid recording media path");
    }
    const origin = new URL(baseUrl).origin;
    const media = new URL(body.path, origin);
    if (media.origin !== origin || !media.pathname.startsWith(expectedPrefix)) {
        throw new Error("Invalid recording media path");
    }
    media.searchParams.set("ticket", body.ticket);
    return { ticket: body.ticket, expiresAt: body.expiresAt, url: media.toString() };
};

/**
 * Fetch one imported attachment's bytes.
 *
 * Returns a Blob rather than a URL on purpose. This route authenticates on the
 * `Authorization` header alone, so the caller must fetch it and build an object
 * URL; the organization token must never be appended to a URL for an `<img>`
 * to load, the way `buildArcStorageUrl` does for the ARC gateway.
 *
 * These bytes come from `fleet_mission_storage`'s own blob store, not from the
 * ARC, so they remain readable when the robot is offline — which is the point
 * of importing them.
 *
 * `null` means the row is visible but its bytes are not:
 *  - 409 while the importer is still copying (`fetch_status` is not "stored")
 *  - 404 when the blob is missing or failed its size check
 * Both are ordinary states for a freshly imported attachment, not errors.
 */
export const getAttachmentBlob = async (
    proxyHostname: string | null | undefined,
    sourceArcSerial: string,
    attachmentId: string,
    signal?: AbortSignal,
): Promise<Blob | null> => {
    const url = buildUrl(
        requireBaseUrl(proxyHostname),
        `/api/attachments/${encodeURIComponent(sourceArcSerial)}/${encodeURIComponent(attachmentId)}/file`,
    );

    let res = await requestOnce(url, signal);
    if (res.status === 401) {
        await refresh(); // throws "401 ..." when there is no usable refresh token
        res = await requestOnce(url, signal);
    }

    if (res.status === 401) throw new Error("Sign in required.");
    if (res.status === 403) throw new Error("You don't have permission to do that.");
    if (res.status === 404 || res.status === 409) return null;
    if (!res.ok) throw await errorFor(res);

    return res.blob();
};
